import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { omit } from '../../util/common';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, LayoutSingleColumn, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters (including location: address, bounds, origin)
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);
    const locationParamNames = ['address', 'bounds', 'origin'];
    const allParamsToReset = [...filterQueryParamNames, ...locationParamNames];

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params (filters + location)
    const queryParams = omit(urlQueryParams, allParamsToReset);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords, origin } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address, bounds, and origin can be updated by the location filter in the sidebar,
        // or by TopbarSearchForm / map. When the location filter submits, use its values.
        const hasLocationUpdate =
          updatedURLParams && Object.prototype.hasOwnProperty.call(updatedURLParams, 'address');
        const addressMaybe = hasLocationUpdate ? updatedURLParams.address : address;
        const boundsMaybe = hasLocationUpdate ? updatedURLParams.bounds : bounds;
        const originMaybe = hasLocationUpdate ? updatedURLParams.origin : origin;
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        const locationParams = {
          ...(addressMaybe != null ? { address: addressMaybe } : {}),
          ...(boundsMaybe != null ? { bounds: boundsMaybe } : {}),
          ...(isOriginInUse(config) && originMaybe != null ? { origin: originMaybe } : {}),
        };
        const { address: _addr, bounds: _bnds, origin: _orig, ...restUpdated } =
          updatedURLParams || {};
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...restUpdated,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              ...locationParams,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig, mainSearch } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
      mainSearch,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      mainSearch,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const hasMultipleListingTypes = activeListingTypes.length > 1;
    const builtInPrimaryFilters = defaultFiltersConfig
      .filter(f => ['categoryLevel', 'listingType'].includes(f.key))
      .filter(f => f.key !== 'listingType' || hasMultipleListingTypes);
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Parse page heading to be included in the title
    const pageHeading = searchInProgress
      ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
      : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      pageHeading
    );

    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <LayoutSingleColumn
          topbar={
            <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
          }
          footer={<FooterContainer />}
        >
          <div className={css.searchPageWrapper}>
            <section className={css.headerSection} aria-labelledby="search-page-title">
              <div className={css.container}>
                <h1 id="search-page-title" className={css.headerTitle}>
                  <FormattedMessage id="SearchPage.chooseBusinessOwnerTitle" />
                </h1>
                <p className={css.headerSubtitle}>
                  <FormattedMessage id="SearchPage.chooseBusinessOwnerSubtitle" />
                </p>
              </div>
            </section>

            <section className={css.contentSection}>
              <div className={css.container}>
                <div className={css.searchGrid}>
                  <aside
                    className={css.filterCard}
                    data-testid="filterColumnAside"
                    aria-label={intl.formatMessage({ id: 'SearchPage.filtersTitle' })}
                  >
                    <h2 className={css.filterCardTitle}>
                      <FormattedMessage id="SearchPage.filtersTitle" />
                    </h2>
                    <p className={css.filterCardSubtitle}>
                      <FormattedMessage id="SearchPage.filtersSubtitle" />
                    </p>
                    <div className={css.filterColumnContent}>
                      {availableFilters.map(filterConfig => {
                        const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                          filterConfig.key
                        }`;
                        const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                        return (
                          <FilterComponent
                            key={key}
                            id={filterId}
                            className={css.filter}
                            config={filterConfig}
                            containerId="SearchPageWithGrid_DesktopFilters"
                            listingCategories={listingCategories}
                            marketplaceCurrency={marketplaceCurrency}
                            urlQueryParams={validQueryParams}
                            initialValues={initialValues(this.props, this.state.currentQueryParams)}
                            getHandleChangedValueFn={this.getHandleChangedValueFn}
                            intl={intl}
                            appConfig={config}
                            liveEdit
                            showAsPopup={false}
                            isDesktop
                          />
                        );
                      })}
                      <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                        <FormattedMessage id="SearchFiltersMobile.resetAll" />
                      </button>
                    </div>
                  </aside>

                  <div id="main-content" className={css.searchMain} role="main">
                    <SearchFiltersMobile
                      className={css.searchFiltersMobileList}
                      urlQueryParams={validQueryParams}
                      sortByComponent={sortBy('mobile')}
                      listingsAreLoaded={listingsAreLoaded}
                      resultsCount={totalItems}
                      searchInProgress={searchInProgress}
                      searchListingsError={searchListingsError}
                      showAsModalMaxWidth={MODAL_BREAKPOINT}
                      onManageDisableScrolling={onManageDisableScrolling}
                      onOpenModal={this.onOpenMobileModal}
                      onCloseModal={this.onCloseMobileModal}
                      resetAll={this.resetAll}
                      selectedFiltersCount={selectedFiltersCountForMobile}
                      isMapVariant={false}
                      noResultsInfo={noResultsInfo}
                      location={location}
                    >
                      {availableFilters.map(filterConfig => {
                        const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                          filterConfig.key
                        }`;
                        const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                        return (
                          <FilterComponent
                            key={key}
                            id={filterId}
                            config={filterConfig}
                            containerId="SearchPage_MobileFilters"
                            listingCategories={listingCategories}
                            marketplaceCurrency={marketplaceCurrency}
                            urlQueryParams={validQueryParams}
                            initialValues={initialValues(this.props, this.state.currentQueryParams)}
                            getHandleChangedValueFn={this.getHandleChangedValueFn}
                            intl={intl}
                            appConfig={config}
                            liveEdit
                            showAsPopup={false}
                          />
                        );
                      })}
                    </SearchFiltersMobile>
                    <MainPanelHeader
                      className={css.mainPanel}
                      sortByComponent={sortBy('desktop')}
                      isSortByActive={sortConfig.active}
                      listingsAreLoaded={listingsAreLoaded}
                      resultsCount={totalItems}
                      searchInProgress={searchInProgress}
                      searchListingsError={searchListingsError}
                      noResultsInfo={noResultsInfo}
                    />
                    <div
                      className={classNames(css.listingsForGridVariant, {
                        [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                      })}
                    >
                      {searchListingsError ? (
                        <H5 className={css.error}>
                          <FormattedMessage id="SearchPage.searchError" />
                        </H5>
                      ) : null}
                      {!isValidDatesFilter ? (
                        <H5>
                          <FormattedMessage id="SearchPage.invalidDatesFilter" />
                        </H5>
                      ) : null}
                      <SearchResultsPanel
                        className={css.searchListingsPanel}
                        listings={listings}
                        pagination={listingsAreLoaded ? pagination : null}
                        search={parse(location.search)}
                        isMapVariant={false}
                        listingTypeParam={listingTypePathParam}
                        intl={intl}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
